import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { sendEmail } from '../emailService'
import { sendWhatsApp } from '../whatsappClient'

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday:'short', day:'numeric', month:'short', year:'numeric'
  })
}
function fmtTime(t) {
  if (!t) return '—'
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}
function fmtDateTime(d, t) {
  if (!d) return '—'
  return `${fmtDate(d)}${t ? ' at ' + fmtTime(t) : ''}`
}

function daysLeft(hardDeleteAt) {
  const ms = new Date(hardDeleteAt) - new Date()
  return Math.max(0, Math.ceil(ms / 86400000))
}

// ── Classes deleted via Bulk Cancel's "Delete" option, still inside their
// 7-day grace period before the daily purge job removes them for good.
function PendingPurgeList() {
  const [pending, setPending] = useState([])
  const [loading, setLoading] = useState(true)
  const [restoringId, setRestoringId] = useState(null)
  const [ok, setOk] = useState('')

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('classes')
      .select('*')
      .not('hard_delete_at', 'is', null)
      .gt('hard_delete_at', new Date().toISOString())
      .order('hard_delete_at')
    setPending(data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function restore(cls) {
    setRestoringId(cls.id)
    await supabase.from('classes').update({
      is_cancelled: false, cancel_reason: null, cancelled_at: null, hard_delete_at: null,
    }).eq('id', cls.id)
    setOk(`✓ "${cls.title}" restored.`)
    setTimeout(() => setOk(''), 4000)
    setRestoringId(null)
    load()
  }

  if (loading) return <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>

  return (
    <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'20px' }}>
      <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'16px' }}>
        Classes deleted via "Delete" are held here for 7 days before a daily job permanently removes them. Restore anything that was a mistake.
      </div>
      {ok && <div style={{ padding:'9px 12px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'8px', fontSize:'13px', color:'#34d399', marginBottom:'12px' }}>{ok}</div>}
      {pending.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a25))', fontSize:'13px' }}>
          <div style={{ fontSize:'36px', marginBottom:'12px' }}>✅</div>
          Nothing pending purge right now.
        </div>
      ) : (
        <div style={{ display:'grid', gap:'6px' }}>
          {pending.map(cls => (
            <div key={cls.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'rgba(239,68,68,0.06)', borderRadius:'10px', border:'0.5px solid rgba(239,68,68,0.15)', flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:'160px' }}>
                <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{cls.title}</div>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>
                  {fmtDate(cls.class_date)} {cls.start_time && `· ${cls.start_time}`} {cls.teacher_name && `· 👨‍🏫 ${cls.teacher_name}`}
                </div>
              </div>
              <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 10px', borderRadius:'8px', background:'rgba(244,163,53,0.15)', color:'#f4a335', flexShrink:0 }}>
                {daysLeft(cls.hard_delete_at)}d left
              </span>
              <button onClick={() => restore(cls)} disabled={restoringId === cls.id}
                style={{ fontSize:'11px', fontWeight:700, padding:'6px 14px', borderRadius:'8px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.3)', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                {restoringId === cls.id ? '⏳' : '↩ Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function BulkCancel() {
  const [view, setView] = useState('bulk') // bulk | pending
  const [students,    setStudents]    = useState([])
  const [teachers,    setTeachers]    = useState([])
  const [selStudent,  setSelStudent]  = useState('')
  const [selTeacher,  setSelTeacher]  = useState('')
  const [filterMode,  setFilterMode]  = useState('teacher') // both | student | teacher
  const [whichCls,    setWhichCls]    = useState('future')  // future | all
  const [sendMail,    setSendMail]    = useState(false)
  const [reason,      setReason]      = useState('')
  const [preview,     setPreview]     = useState([])
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [busy,        setBusy]        = useState(false)
  const [done,        setDone]        = useState(null)
  const [err,         setErr]         = useState('')
  const [step,        setStep]        = useState(1)
  const [deleteMode,  setDeleteMode]  = useState(false) // delete vs cancel
  const [kept,        setKept]        = useState(new Set()) // class ids ticked "keep" — skipped by the bulk action

  // ── Per-class box (expand a single class in the preview list to manage
  // it individually instead of going through the bulk multi-select flow) ──
  const [expanded,    setExpanded]    = useState(null) // class id currently expanded
  const [rowAction,   setRowAction]   = useState(null) // 'edit' | 'reschedule' | 'delete' | null
  const [rowBusy,     setRowBusy]     = useState(false)
  const [rowErr,      setRowErr]      = useState('')
  const [editTitle,      setEditTitle]      = useState('')
  const [editDate,       setEditDate]       = useState('')
  const [editTime,       setEditTime]       = useState('')
  const [editTeacherId,  setEditTeacherId]  = useState('')
  const [editMeetLink,   setEditMeetLink]   = useState('')
  const [rsDate,      setRsDate]      = useState('')
  const [rsTime,      setRsTime]      = useState('')
  const [rsReason,    setRsReason]    = useState('')
  const [rsNotify,    setRsNotify]    = useState(true)
  const [delReason,   setDelReason]   = useState('')
  const [delNotify,   setDelNotify]   = useState(true)

  function toggleKeep(id) {
    setKept(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  useEffect(() => { loadPeople() }, [])

  async function loadPeople() {
    const [{ data: s }, { data: t }] = await Promise.all([
      supabase.from('profiles').select('id,full_name,email,phone,timezone').eq('role','student').order('full_name'),
      supabase.from('profiles').select('id,full_name,email,phone').eq('role','teacher').order('full_name'),
    ])
    setStudents(s || [])
    setTeachers(t || [])
  }

  async function loadPreview() {
    setErr('')
    if (filterMode === 'both'    && (!selStudent || !selTeacher)) return setErr('Select both student and teacher.')
    if (filterMode === 'student' && !selStudent)                  return setErr('Select a student.')
    if (filterMode === 'teacher' && !selTeacher)                  return setErr('Select a teacher.')

    setLoadingPrev(true)
    const today = new Date().toISOString().slice(0,10)

    try {
      // Build base query by teacher first (most reliable)
      let q = supabase.from('classes').select('*')

      if (filterMode === 'teacher') {
        // Simple: all classes for this teacher
        q = q.eq('teacher_id', selTeacher)

      } else if (filterMode === 'student') {
        // Get all class IDs this student is enrolled in
        const { data: enrols } = await supabase
          .from('enrollments').select('class_id').eq('student_id', selStudent)
        const classIds = (enrols || []).map(e => e.class_id).filter(Boolean)
        if (!classIds.length) {
          setPreview([]); setLoadingPrev(false); setStep(2)
          setErr('This student has no enrollments found. Try "By Teacher" mode instead.')
          return
        }
        q = q.in('id', classIds)

      } else if (filterMode === 'both') {
        // Get teacher's classes, then cross-check with student's batch/enrollments
        // Primary filter: teacher — then we'll filter further by student match
        q = q.eq('teacher_id', selTeacher)
      }

      // Date filter
      if (whichCls === 'future') q = q.gte('class_date', today)
      q = q.order('class_date').order('start_time')

      const { data: cls, error: clsErr } = await q
      if (clsErr) throw new Error(clsErr.message)

      let result = (cls || []).filter(c => !c.is_cancelled)

      // For 'both' mode: only ever touch classes where the student is actually
      // enrolled. Previously this silently fell back to "all of the teacher's
      // classes" when no enrollment was found — meaning picking a teacher +
      // student with no enrollment record between them could wipe out the
      // teacher's ENTIRE schedule while looking like it was scoped to one
      // student. Never expand scope silently — show nothing instead.
      if (filterMode === 'both' && selStudent) {
        const { data: enrols } = await supabase
          .from('enrollments').select('class_id').eq('student_id', selStudent)
        const enrolledIds = new Set((enrols || []).map(e => e.class_id).filter(Boolean))
        result = result.filter(c => enrolledIds.has(c.id))
      }

      // Attach enrolled students to each class — needed to show "who's in
      // this class" per row (especially in "By Teacher" mode, which has no
      // single student in context) and to notify the right people when a
      // single class is edited/rescheduled/deleted from its own box below.
      if (result.length) {
        const resultIds = result.map(c => c.id)
        const { data: enrolRows } = await supabase.from('enrollments')
          .select('class_id, student_id, profiles:student_id(full_name,email,phone,timezone)')
          .in('class_id', resultIds)
        const byClass = {}
        for (const row of (enrolRows || [])) {
          if (!row.profiles) continue
          if (!byClass[row.class_id]) byClass[row.class_id] = []
          byClass[row.class_id].push({ student_id: row.student_id, ...row.profiles })
        }
        result = result.map(c => ({ ...c, enrolledStudents: byClass[c.id] || [] }))
      }

      setPreview(result)
      setKept(new Set())
      setExpanded(null); setRowAction(null); setRowErr('')
      if (result.length === 0) {
        setErr(filterMode === 'both'
          ? `No classes found where both ${teachers.find(t=>t.id===selTeacher)?.full_name || 'this teacher'} and ${students.find(s=>s.id===selStudent)?.full_name || 'this student'} match — no enrollment link between them was found. Nothing will be affected. If you meant to target all of this teacher's classes, use "By Teacher" mode instead.`
          : 'No active classes found.')
      } else {
        setErr('')
      }
    } catch(e) {
      setErr('Error loading classes: ' + e.message)
    }
    setLoadingPrev(false)
    setStep(2)
  }

  async function bulkCancel() {
    const toAction = preview.filter(c => !kept.has(c.id))
    if (!toAction.length) return setErr('Nothing to do — every class is ticked "keep".')
    setBusy(true); setErr('')

    const student = students.find(s => s.id === selStudent)
    const teacher = teachers.find(t => t.id === selTeacher)
    const ids = toAction.map(c => c.id)

    try {
      if (deleteMode) {
        // ── DELETE, WITH A 7-DAY GRACE PERIOD ─────────────────────────────────
        // Doesn't touch the row yet — just hides it (is_cancelled) and marks
        // when it becomes eligible for the daily purge job to actually
        // remove it. Restorable from the "Pending Purge" tab until then.
        const hardDeleteAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        const { error } = await supabase.from('classes').update({
          is_cancelled:  true,
          cancel_reason: reason.trim() || 'Bulk deleted by admin — pending permanent purge',
          cancelled_at:  new Date().toISOString(),
          hard_delete_at: hardDeleteAt,
        }).in('id', ids)
        if (error) throw new Error(error.message)
      } else {
        // ── SOFT CANCEL — try with is_cancelled column ───────────────────────
        // Try updating with is_cancelled first
        const updatePayload = {
          is_cancelled:  true,
          cancel_reason: reason.trim() || 'Bulk cancelled by admin',
          cancelled_at:  new Date().toISOString(),
        }

        const { error: updateErr } = await supabase
          .from('classes').update(updatePayload).in('id', ids)

        // If is_cancelled column doesn't exist — fall back to hard delete
        if (updateErr) {
          if (updateErr.message?.includes('is_cancelled') || updateErr.message?.includes('column')) {
            // Column doesn't exist — just delete instead
            const { error: delErr } = await supabase.from('classes').delete().in('id', ids)
            if (delErr) throw new Error(delErr.message)
            for (const c of toAction) {
              await supabase.from('events').delete().eq('title', c.title)
            }
          } else {
            throw new Error(updateErr.message)
          }
        }
      }

      // Send cancellation email + WhatsApp if requested
      if (sendMail && student?.email) {
        await sendEmail('class_cancelled', student.email, {
          name:        student.full_name,
          classTitle:  `${toAction.length} classes cancelled`,
          classDate:   `${fmtDate(toAction[0]?.class_date)} — ${fmtDate(toAction[toAction.length-1]?.class_date)}`,
          startTime:   null,
          teacherName: teacher?.full_name || null,
          zoomLink:    null,
          reason:      reason.trim() || 'Cancelled by admin',
        })
      }
      // class_cancelled_wa is a POSITIONAL template — array order must
      // match {{1}}..{{4}}: name, class_name, date_time, reason.
      if (sendMail && student?.phone) {
        sendWhatsApp('class_cancelled_wa', student.phone, [
          student.full_name,
          `${toAction.length} classes`,
          `${fmtDate(toAction[0]?.class_date)} — ${fmtDate(toAction[toAction.length-1]?.class_date)}`,
          reason.trim() || 'Cancelled by admin',
        ])
      }
      if (teacher?.phone) {
        sendWhatsApp('class_cancelled_wa', teacher.phone, [
          teacher.full_name,
          `${toAction.length} classes`,
          `${fmtDate(toAction[0]?.class_date)} — ${fmtDate(toAction[toAction.length-1]?.class_date)}`,
          reason.trim() || 'Cancelled by admin',
        ])
      }

      setDone({
        count:       toAction.length,
        keptCount:   kept.size,
        studentName: student?.full_name,
        teacherName: teacher?.full_name,
        emailSent:   sendMail && !!student?.email,
        deleted:     deleteMode,
      })
      setStep(3)
    } catch(e) {
      setErr('Cancel failed: ' + e.message)
    }
    setBusy(false)
  }

  function reset() {
    setStep(1); setPreview([]); setDone(null); setErr(''); setKept(new Set())
    setSelStudent(''); setSelTeacher(''); setReason('')
    setExpanded(null); setRowAction(null); setRowErr('')
  }

  // ── Single-class box actions ──────────────────────────────────────────
  function openRow(cls, action) {
    setExpanded(cls.id); setRowAction(action); setRowErr('')
    if (action === 'edit') {
      setEditTitle(cls.title || ''); setEditDate(cls.class_date || ''); setEditTime(cls.start_time || '')
      setEditTeacherId(cls.teacher_id || ''); setEditMeetLink(cls.meet_link || '')
    } else if (action === 'reschedule') {
      setRsDate(''); setRsTime(cls.start_time || ''); setRsReason(''); setRsNotify(true)
    } else if (action === 'delete') {
      setDelReason(''); setDelNotify(true)
    }
  }
  function closeRow() { setExpanded(null); setRowAction(null); setRowErr('') }

  async function saveEdit(cls) {
    if (!editDate) return setRowErr('Date is required.')
    setRowBusy(true); setRowErr('')
    const teacherObj = teachers.find(t => t.id === editTeacherId)
    const { error } = await supabase.from('classes').update({
      title:        editTitle.trim() || cls.title,
      class_date:   editDate,
      start_time:   editTime || null,
      teacher_id:   editTeacherId || null,
      teacher_name: teacherObj?.full_name || null,
      meet_link:    editMeetLink.trim() || null,
    }).eq('id', cls.id)
    if (error) { setRowErr(error.message); setRowBusy(false); return }
    if (editDate !== cls.class_date) {
      const d = new Date(editDate + 'T00:00:00')
      await supabase.from('events').update({
        day: d.getDate(), month: d.getMonth()+1, year: d.getFullYear(), time: editTime || null,
      }).eq('class_id', cls.id)
    }
    setRowBusy(false); closeRow(); loadPreview()
  }

  async function saveReschedule(cls) {
    if (!rsDate) return setRowErr('Pick a new date.')
    setRowBusy(true); setRowErr('')
    const { error } = await supabase.from('classes').update({
      class_date: rsDate,
      start_time: rsTime || null,
      rescheduled_from_date: cls.class_date,
      rescheduled_from_time: cls.start_time || null,
    }).eq('id', cls.id)
    if (error) { setRowErr(error.message); setRowBusy(false); return }

    const d = new Date(rsDate + 'T00:00:00')
    await supabase.from('events').update({
      day: d.getDate(), month: d.getMonth()+1, year: d.getFullYear(), time: rsTime || null,
    }).eq('class_id', cls.id)

    if (rsNotify) {
      const teacherObj = teachers.find(t => t.id === cls.teacher_id)
      const oldWhen = fmtDateTime(cls.class_date, cls.start_time)
      const newWhen = fmtDateTime(rsDate, rsTime)
      const titleWithReason = rsReason.trim() ? `${cls.title} (${rsReason.trim()})` : cls.title
      for (const stu of (cls.enrolledStudents || [])) {
        if (stu.email) sendEmail('class_scheduled', stu.email, {
          name: stu.full_name, classTitle: titleWithReason, classDate: fmtDate(rsDate), startTime: rsTime || null,
          teacherName: teacherObj?.full_name || cls.teacher_name || null, zoomLink: cls.meet_link || null, studentTZ: stu.timezone,
        })
        if (stu.phone) sendWhatsApp('class_rescheduled_wa', stu.phone, [stu.full_name, titleWithReason, oldWhen, newWhen])
      }
      if (teacherObj?.email) sendEmail('class_scheduled', teacherObj.email, {
        name: teacherObj.full_name, classTitle: `Rescheduled: ${titleWithReason}`, classDate: fmtDate(rsDate), startTime: rsTime || null,
        teacherName: teacherObj.full_name, zoomLink: cls.meet_link || null,
      })
      if (teacherObj?.phone) sendWhatsApp('class_rescheduled_wa', teacherObj.phone, [teacherObj.full_name, titleWithReason, oldWhen, newWhen])
    }
    setRowBusy(false); closeRow(); loadPreview()
  }

  async function deleteSingleClass(cls) {
    setRowBusy(true); setRowErr('')
    const hardDeleteAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    const { error } = await supabase.from('classes').update({
      is_cancelled:   true,
      cancel_reason:  delReason.trim() || 'Deleted by admin — pending permanent purge',
      cancelled_at:   new Date().toISOString(),
      hard_delete_at: hardDeleteAt,
    }).eq('id', cls.id)
    if (error) { setRowErr(error.message); setRowBusy(false); return }

    if (delNotify) {
      const teacherObj = teachers.find(t => t.id === cls.teacher_id)
      const when = fmtDateTime(cls.class_date, cls.start_time)
      const reasonText = delReason.trim() || 'Cancelled by admin'
      for (const stu of (cls.enrolledStudents || [])) {
        if (stu.email) sendEmail('class_cancelled', stu.email, {
          name: stu.full_name, classTitle: cls.title, classDate: fmtDate(cls.class_date), startTime: cls.start_time || null,
          teacherName: teacherObj?.full_name || cls.teacher_name || null, zoomLink: null, reason: reasonText,
        })
        if (stu.phone) sendWhatsApp('class_cancelled_wa', stu.phone, [stu.full_name, cls.title, when, reasonText])
      }
      if (teacherObj?.phone) sendWhatsApp('class_cancelled_wa', teacherObj.phone, [teacherObj.full_name, cls.title, when, reasonText])
    }
    setRowBusy(false); closeRow(); loadPreview()
  }

  const student = students.find(s => s.id === selStudent)
  const teacher = teachers.find(t => t.id === selTeacher)

  const inp = {
    width:'100%', background:'rgba(var(--fg-rgb),0.06)',
    border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px',
    padding:'9px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))',
    outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box',
  }
  const lbl = {
    display:'block', fontSize:'10px', fontWeight:700,
    color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em',
    textTransform:'uppercase', marginBottom:'5px', marginTop:'12px',
  }
  const modeBtn = (id, label, desc) => (
    <button type="button" onClick={() => setFilterMode(id)}
      style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'10px', border:`1px solid ${filterMode===id?'#1e90ff':'rgba(var(--fg-rgb),0.07)'}`, background:filterMode===id?'rgba(30,144,255,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left', width:'100%' }}>
      <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:`2px solid ${filterMode===id?'#1e90ff':'rgba(var(--fg-rgb),var(--a20))'}`, background:filterMode===id?'#1e90ff':'transparent', flexShrink:0 }}/>
      <div>
        <div style={{ fontSize:'12px', fontWeight:700, color:filterMode===id?'var(--fg-solid)':'rgba(var(--fg-rgb),var(--a60))' }}>{label}</div>
        <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{desc}</div>
      </div>
    </button>
  )

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>🚫 Bulk Cancel / Delete Classes</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'16px' }}>Cancel or delete multiple classes at once by teacher or student.</div>

      <div style={{ display:'flex', gap:'6px', marginBottom:'20px', flexWrap:'wrap' }}>
        {[{ id:'bulk', label:'🚫 Bulk Cancel / Delete' }, { id:'pending', label:'🗑 Pending Purge' }].map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{
            fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'10px', border:'none',
            cursor:'pointer', fontFamily:'inherit',
            background: view===t.id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)',
            color:      view===t.id ? '#fff'    : 'rgba(var(--fg-rgb),var(--a45))',
          }}>{t.label}</button>
        ))}
      </div>

      {view === 'pending' && <PendingPurgeList />}

      {view === 'bulk' && <>
      {/* ── STEP 1: Filters ── */}
      {step === 1 && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
          <div>
            {/* Filter mode */}
            <label style={lbl}>Filter By</label>
            <div style={{ display:'grid', gap:'6px', marginBottom:'4px' }}>
              {modeBtn('teacher', 'By Teacher',         'Cancel all classes for a specific teacher')}
              {modeBtn('student', 'By Student',         'Cancel classes a student is enrolled in')}
              {modeBtn('both',    'Teacher + Student',  'Cancel where both match')}
            </div>

            {/* Teacher select */}
            {(filterMode === 'teacher' || filterMode === 'both') && (
              <>
                <label style={lbl}>Select Teacher *</label>
                <select style={{ ...inp, colorScheme:'dark' }} value={selTeacher} onChange={e => setSelTeacher(e.target.value)}>
                  <option value="">— Choose teacher —</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </>
            )}

            {/* Student select */}
            {(filterMode === 'student' || filterMode === 'both') && (
              <>
                <label style={lbl}>Select Student *</label>
                <select style={{ ...inp, colorScheme:'dark' }} value={selStudent} onChange={e => setSelStudent(e.target.value)}>
                  <option value="">— Choose student —</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </>
            )}

            {/* Which classes */}
            <label style={lbl}>Which Classes</label>
            <div style={{ display:'grid', gap:'6px' }}>
              {[
                { v:'future', label:'Future classes only',       desc:'Only upcoming classes from today' },
                { v:'all',    label:'All classes (past + future)', desc:'Complete history'              },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setWhichCls(o.v)}
                  style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'10px', border:`1px solid ${whichCls===o.v?'#1e90ff':'rgba(var(--fg-rgb),0.07)'}`, background:whichCls===o.v?'rgba(30,144,255,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left', width:'100%' }}>
                  <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:`2px solid ${whichCls===o.v?'#1e90ff':'rgba(var(--fg-rgb),var(--a20))'}`, background:whichCls===o.v?'#1e90ff':'transparent', flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:700, color:whichCls===o.v?'var(--fg-solid)':'rgba(var(--fg-rgb),var(--a60))' }}>{o.label}</div>
                    <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{o.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Action type */}
            <label style={lbl}>Action</label>
            <div style={{ display:'grid', gap:'6px' }}>
              <button type="button" onClick={() => setDeleteMode(false)}
                style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'10px', border:`1px solid ${!deleteMode?'#f4a335':'rgba(var(--fg-rgb),0.07)'}`, background:!deleteMode?'rgba(244,163,53,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left' }}>
                <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:`2px solid ${!deleteMode?'#f4a335':'rgba(var(--fg-rgb),var(--a20))'}`, background:!deleteMode?'#f4a335':'transparent', flexShrink:0 }}/>
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:!deleteMode?'#f4a335':'rgba(var(--fg-rgb),var(--a60))' }}>Mark as Cancelled</div>
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>Soft cancel — keeps record, marks is_cancelled = true</div>
                </div>
              </button>
              <button type="button" onClick={() => setDeleteMode(true)}
                style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'10px', border:`1px solid ${deleteMode?'#f87171':'rgba(var(--fg-rgb),0.07)'}`, background:deleteMode?'rgba(239,68,68,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left' }}>
                <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:`2px solid ${deleteMode?'#f87171':'rgba(var(--fg-rgb),var(--a20))'}`, background:deleteMode?'#f87171':'transparent', flexShrink:0 }}/>
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:deleteMode?'#f87171':'rgba(var(--fg-rgb),var(--a60))' }}>Delete (7-day grace period)</div>
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>Hidden immediately, restorable for 7 days, then permanently purged</div>
                </div>
              </button>
            </div>

            {/* Reason */}
            <label style={lbl}>Reason (optional)</label>
            <input style={inp} type="text" placeholder="e.g. Teacher resigned, Batch closed..." value={reason} onChange={e => setReason(e.target.value)}/>

            {/* Email toggle */}
            {(filterMode === 'student' || filterMode === 'both') && selStudent && (
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'12px', padding:'10px 12px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px' }}>
                <button type="button" onClick={() => setSendMail(!sendMail)}
                  style={{ width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer', position:'relative', background:sendMail?'#1e90ff':'rgba(var(--fg-rgb),0.1)', transition:'background 0.2s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:'2px', left:sendMail?'18px':'2px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                </button>
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))' }}>Send cancellation email to student</div>
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{student?.email || 'No email on file'}</div>
                </div>
              </div>
            )}

            {err && <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}

            <button onClick={loadPreview} disabled={loadingPrev}
              style={{ marginTop:'16px', width:'100%', padding:'12px', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:loadingPrev?'not-allowed':'pointer', fontFamily:'inherit' }}>
              {loadingPrev ? '⏳ Loading classes...' : 'Preview Classes →'}
            </button>
          </div>

          {/* Right: summary */}
          <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'20px' }}>
            <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'14px', letterSpacing:'0.08em' }}>SELECTED FILTERS</div>
            {[
              { label:'Mode',    value: filterMode==='both'?'Student + Teacher':filterMode==='student'?'Student only':'Teacher only' },
              { label:'Student', value: student?.full_name || '—' },
              { label:'Teacher', value: teacher?.full_name || '—' },
              { label:'Classes', value: whichCls==='future'?'Future only':'All classes' },
              { label:'Action',  value: deleteMode?'🗑 Hard delete':'🚫 Soft cancel' },
              { label:'Email',   value: sendMail?'Yes — send email':'No — silent' },
            ].map(r => (
              <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'8px', marginBottom:'4px' }}>
                <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))' }}>{r.label}</span>
                <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a80))', fontWeight:500 }}>{r.value}</span>
              </div>
            ))}
            <div style={{ marginTop:'16px', padding:'12px', background:'rgba(244,163,53,0.07)', border:'0.5px solid rgba(244,163,53,0.2)', borderRadius:'10px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a45))', lineHeight:1.65 }}>
              💡 <strong style={{color:'#f4a335'}}>Mark as Cancelled</strong> keeps classes in the database, just hidden as cancelled.<br/>
              <strong style={{color:'#f87171'}}>Delete</strong> hides them immediately and restorable from "Pending Purge" for 7 days — after that they're permanently removed.
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 2: Preview & Confirm ── */}
      {step === 2 && (() => {
        const toActionCount = preview.length - kept.size
        return (
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'20px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
            <div>
              <div style={{ fontSize:'15px', fontWeight:800, color: deleteMode?'#f87171':'#f4a335' }}>
                {deleteMode ? '🗑' : '🚫'} {toActionCount} classes will be {deleteMode?'deleted':'cancelled'}
                {kept.size > 0 && <span style={{ fontSize:'12px', fontWeight:700, color:'#1e90ff', marginLeft:'8px' }}>· {kept.size} kept</span>}
              </div>
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'3px' }}>
                {student?.full_name && `Student: ${student.full_name}`}
                {student && teacher && ' · '}
                {teacher?.full_name && `Teacher: ${teacher.full_name}`}
              </div>
            </div>
            <button onClick={() => setStep(1)}
              style={{ fontSize:'12px', padding:'6px 14px', borderRadius:'8px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
              ← Change filters
            </button>
          </div>

          {preview.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a25))', fontSize:'13px' }}>
              <div style={{ fontSize:'36px', marginBottom:'12px' }}>✅</div>
              No classes match the selected filters.
            </div>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))' }}>✓ Tick any class to <strong style={{color:'#1e90ff'}}>keep</strong> it — it'll be skipped.</div>
                {kept.size > 0 && (
                  <button type="button" onClick={() => setKept(new Set())}
                    style={{ fontSize:'11px', fontWeight:700, color:'#1e90ff', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:0 }}>
                    Clear kept ({kept.size})
                  </button>
                )}
              </div>

              <div style={{ background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', overflow:'hidden', marginBottom:'16px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'34px 34px 1.7fr 1.1fr 1.3fr 88px', gap:'8px', padding:'10px 16px', background:'rgba(var(--fg-rgb),0.05)', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.08)' }}>
                  {['', '#', 'Class', 'Date & Time', 'Student(s)', ''].map((h, hi) => (
                    <div key={hi} style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.08em', textTransform:'uppercase' }}>{h}</div>
                  ))}
                </div>
                <div style={{ maxHeight:'480px', overflowY:'auto' }}>
                {preview.map((cls, i) => {
                  const isKept = kept.has(cls.id)
                  const isOpen = expanded === cls.id
                  const inpSm = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'8px', padding:'8px 10px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
                  return (
                  <div key={cls.id} style={{ background: isKept ? 'rgba(var(--fg-rgb),0.015)' : 'transparent', borderBottom: i < preview.length-1 ? '0.5px solid rgba(var(--fg-rgb),0.05)' : 'none', opacity: isKept ? 0.55 : 1 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'34px 34px 1.7fr 1.1fr 1.3fr 88px', gap:'8px', alignItems:'center', padding:'9px 16px' }}>
                      <button type="button" onClick={() => toggleKeep(cls.id)}
                        title={isKept ? 'Kept — click to include again' : 'Click to keep this class'}
                        style={{ width:'20px', height:'20px', borderRadius:'6px', border:`1.5px solid ${isKept?'#1e90ff':'rgba(var(--fg-rgb),var(--a20))'}`, background:isKept?'#1e90ff':'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, fontSize:'12px', color:'#fff', fontWeight:800, padding:0 }}>
                        {isKept ? '✓' : ''}
                      </button>
                      <span style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a40))' }}>#{i+1}</span>
                      <div style={{ minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))', textDecoration: isKept ? 'line-through' : 'none' }} title={cls.title}>
                        {cls.title} {cls.is_cancelled && <span style={{ color:'#f87171', fontWeight:700, fontSize:'10px' }}>· Cancelled</span>}
                      </div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a45))' }}>
                        {fmtDate(cls.class_date)}{cls.start_time && <><br/>{cls.start_time}</>}
                      </div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a45))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={cls.enrolledStudents?.map(s=>s.full_name).join(', ')}>
                        {cls.enrolledStudents?.length > 0 ? `🎓 ${cls.enrolledStudents.map(s => s.full_name).join(', ')}` : '—'}
                      </div>
                      <button type="button" onClick={() => setExpanded(isOpen ? null : cls.id)}
                        title="Manage this class individually"
                        style={{ fontSize:'11px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', background: isOpen ? 'rgba(var(--fg-rgb),0.1)' : 'rgba(30,144,255,0.12)', color: isOpen ? 'rgba(var(--fg-rgb),var(--a50))' : '#5aabff', border:'none', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                        {isOpen ? '▴ Close' : '▾ Manage'}
                      </button>
                    </div>

                    {isOpen && (
                      <div style={{ padding:'0 16px 14px 16px' }}>
                        <div style={{ borderTop:'0.5px solid rgba(var(--fg-rgb),0.08)', paddingTop:'12px' }}>
                          {cls.enrolledStudents?.length > 0 && (
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'10px' }}>
                              {cls.enrolledStudents.map(s => (
                                <span key={s.student_id} style={{ fontSize:'11px', fontWeight:600, padding:'4px 10px', borderRadius:'20px', background:'rgba(16,185,129,0.12)', color:'#34d399' }}>🎓 {s.full_name}</span>
                              ))}
                            </div>
                          )}

                          {!rowAction && (
                            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                              <button type="button" onClick={() => openRow(cls, 'edit')} style={{ fontSize:'11px', fontWeight:700, padding:'7px 12px', borderRadius:'8px', background:'rgba(30,144,255,0.12)', color:'#5aabff', border:'none', cursor:'pointer', fontFamily:'inherit' }}>✏️ Edit</button>
                              <button type="button" onClick={() => openRow(cls, 'reschedule')} style={{ fontSize:'11px', fontWeight:700, padding:'7px 12px', borderRadius:'8px', background:'rgba(139,92,246,0.15)', color:'#a78bfa', border:'none', cursor:'pointer', fontFamily:'inherit' }}>🔄 Reschedule</button>
                              <button type="button" onClick={() => openRow(cls, 'delete')} style={{ fontSize:'11px', fontWeight:700, padding:'7px 12px', borderRadius:'8px', background:'rgba(239,68,68,0.12)', color:'#f87171', border:'none', cursor:'pointer', fontFamily:'inherit' }}>🗑 Delete</button>
                            </div>
                          )}

                          {rowAction === 'edit' && (
                            <div>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
                                <input style={inpSm} type="text" placeholder="Title" value={editTitle} onChange={e=>setEditTitle(e.target.value)} />
                                <select style={inpSm} value={editTeacherId} onChange={e=>setEditTeacherId(e.target.value)}>
                                  <option value="">— No teacher —</option>
                                  {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                                </select>
                                <input style={inpSm} type="date" value={editDate} onChange={e=>setEditDate(e.target.value)} />
                                <input style={inpSm} type="time" value={editTime} onChange={e=>setEditTime(e.target.value)} />
                              </div>
                              <input style={{ ...inpSm, marginBottom:'8px' }} type="text" placeholder="Meet link" value={editMeetLink} onChange={e=>setEditMeetLink(e.target.value)} />
                              {rowErr && <div style={{ marginBottom:'8px', padding:'7px 10px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'7px', fontSize:'11px' }}>{rowErr}</div>}
                              <div style={{ display:'flex', gap:'8px' }}>
                                <button type="button" onClick={() => saveEdit(cls)} disabled={rowBusy} style={{ fontSize:'11px', fontWeight:700, padding:'7px 14px', borderRadius:'8px', background:'#1e90ff', color:'#fff', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: rowBusy?0.6:1 }}>{rowBusy?'Saving...':'Save'}</button>
                                <button type="button" onClick={closeRow} style={{ fontSize:'11px', fontWeight:700, padding:'7px 14px', borderRadius:'8px', background:'rgba(var(--fg-rgb),0.08)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                              </div>
                            </div>
                          )}

                          {rowAction === 'reschedule' && (
                            <div>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
                                <input style={inpSm} type="date" value={rsDate} onChange={e=>setRsDate(e.target.value)} min={new Date().toISOString().slice(0,10)} />
                                <input style={inpSm} type="time" value={rsTime} onChange={e=>setRsTime(e.target.value)} />
                              </div>
                              <input style={{ ...inpSm, marginBottom:'8px' }} type="text" placeholder="Reason (optional)" value={rsReason} onChange={e=>setRsReason(e.target.value)} />
                              <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'8px', cursor:'pointer' }}>
                                <input type="checkbox" checked={rsNotify} onChange={e=>setRsNotify(e.target.checked)} /> Notify student(s) + teacher
                              </label>
                              {rowErr && <div style={{ marginBottom:'8px', padding:'7px 10px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'7px', fontSize:'11px' }}>{rowErr}</div>}
                              <div style={{ display:'flex', gap:'8px' }}>
                                <button type="button" onClick={() => saveReschedule(cls)} disabled={rowBusy} style={{ fontSize:'11px', fontWeight:700, padding:'7px 14px', borderRadius:'8px', background:'#8b5cf6', color:'#fff', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: rowBusy?0.6:1 }}>{rowBusy?'Saving...':'Reschedule'}</button>
                                <button type="button" onClick={closeRow} style={{ fontSize:'11px', fontWeight:700, padding:'7px 14px', borderRadius:'8px', background:'rgba(var(--fg-rgb),0.08)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                              </div>
                            </div>
                          )}

                          {rowAction === 'delete' && (
                            <div>
                              <input style={{ ...inpSm, marginBottom:'8px' }} type="text" placeholder="Reason (optional)" value={delReason} onChange={e=>setDelReason(e.target.value)} />
                              <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'8px', cursor:'pointer' }}>
                                <input type="checkbox" checked={delNotify} onChange={e=>setDelNotify(e.target.checked)} /> Notify student(s) + teacher
                              </label>
                              <div style={{ marginBottom:'8px', padding:'7px 10px', background:'rgba(239,68,68,0.08)', color:'#f87171', borderRadius:'7px', fontSize:'11px' }}>
                                ⚠ Hidden immediately, restorable from "Pending Purge" for 7 days, then permanently removed.
                              </div>
                              {rowErr && <div style={{ marginBottom:'8px', padding:'7px 10px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'7px', fontSize:'11px' }}>{rowErr}</div>}
                              <div style={{ display:'flex', gap:'8px' }}>
                                <button type="button" onClick={() => deleteSingleClass(cls)} disabled={rowBusy} style={{ fontSize:'11px', fontWeight:700, padding:'7px 14px', borderRadius:'8px', background:'#ef4444', color:'#fff', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: rowBusy?0.6:1 }}>{rowBusy?'Deleting...':'Confirm Delete'}</button>
                                <button type="button" onClick={closeRow} style={{ fontSize:'11px', fontWeight:700, padding:'7px 14px', borderRadius:'8px', background:'rgba(var(--fg-rgb),0.08)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  )
                })}
                </div>
              </div>

              <div style={{ padding:'12px 14px', background: deleteMode?'rgba(239,68,68,0.08)':'rgba(244,163,53,0.08)', border:`0.5px solid ${deleteMode?'rgba(239,68,68,0.25)':'rgba(244,163,53,0.25)'}`, borderRadius:'10px', marginBottom:'14px', fontSize:'12px', color: deleteMode?'#f87171':'#f4a335' }}>
                {deleteMode
                  ? `⚠ This will hide ${toActionCount} of ${preview.length} classes now. They're restorable from "Pending Purge" for 7 days, then permanently deleted.`
                  : `🚫 This will mark ${toActionCount} of ${preview.length} classes as cancelled.`}
                {kept.size > 0 && <span> {kept.size} ticked class{kept.size===1?'':'es'} will be left untouched.</span>}
                {sendMail && student?.email && <span> A cancellation email will be sent to <strong>{student.email}</strong>.</span>}
              </div>

              {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px', marginBottom:'10px' }}>{err}</div>}

              <button onClick={bulkCancel} disabled={busy || toActionCount === 0}
                style={{ width:'100%', padding:'13px', background:(busy||toActionCount===0)?'rgba(100,100,100,0.3)': deleteMode?'linear-gradient(135deg,#ef4444,#dc2626)':'linear-gradient(135deg,#f4a335,#e87c1e)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:(busy||toActionCount===0)?'not-allowed':'pointer', fontFamily:'inherit' }}>
                {busy ? '⏳ Processing...' : toActionCount === 0 ? 'Nothing to do — all classes kept' : deleteMode ? `🗑 Delete ${toActionCount} Classes (7-day grace period)` : `🚫 Cancel ${toActionCount} Classes`}
              </button>
            </>
          )}
        </div>
        )
      })()}

      {/* ── STEP 3: Done ── */}
      {step === 3 && done && (
        <div style={{ textAlign:'center', padding:'3rem', background: done.deleted?'rgba(239,68,68,0.06)':'rgba(244,163,53,0.06)', border:`0.5px solid ${done.deleted?'rgba(239,68,68,0.2)':'rgba(244,163,53,0.2)'}`, borderRadius:'16px' }}>
          <div style={{ fontSize:'52px', marginBottom:'14px' }}>{done.deleted?'🗑':'🚫'}</div>
          <div style={{ fontSize:'18px', fontWeight:800, color: done.deleted?'#f87171':'#f4a335', marginBottom:'8px' }}>
            {done.deleted ? 'Classes Hidden — 7-Day Grace Period Started' : 'Bulk Cancel Complete!'}
          </div>
          {done.deleted && (
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'14px', maxWidth:'380px', marginLeft:'auto', marginRight:'auto' }}>
              These are restorable from the "Pending Purge" tab for the next 7 days, after which they're permanently deleted.
            </div>
          )}
          {done.keptCount > 0 && (
            <div style={{ fontSize:'12px', color:'#1e90ff', marginBottom:'14px' }}>
              ✓ {done.keptCount} ticked class{done.keptCount===1?'':'es'} left untouched.
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', maxWidth:'320px', margin:'0 auto 20px' }}>
            <div style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'12px', padding:'14px' }}>
              <div style={{ fontSize:'28px', fontWeight:800, color: done.deleted?'#f87171':'#f4a335' }}>{done.count}</div>
              <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'4px' }}>Classes {done.deleted?'hidden':'cancelled'}</div>
            </div>
            <div style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'12px', padding:'14px' }}>
              <div style={{ fontSize:'28px', fontWeight:800, color:done.emailSent?'#34d399':'#94a3b8' }}>{done.emailSent?'✓':'—'}</div>
              <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'4px' }}>{done.emailSent?'Email sent':'No email'}</div>
            </div>
          </div>
          {(done.studentName || done.teacherName) && (
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))', marginBottom:'20px' }}>
              {done.studentName && `Student: ${done.studentName}`}
              {done.studentName && done.teacherName && ' · '}
              {done.teacherName && `Teacher: ${done.teacherName}`}
            </div>
          )}
          <button onClick={reset}
            style={{ padding:'11px 28px', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', fontSize:'13px', fontWeight:700, border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>
            Cancel / Delete More
          </button>
        </div>
      )}
      </>}
    </div>
  )
}
